import { graphQLIdGenerator } from 'helpers/graphql-utils';
import steven, { ELEMENT_TYPES } from 'helpers/steven';
import { useCurrentCompany, useCurrentUser } from 'javascripts/hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LD_DOCUMENTS_DOCUMENT_BUILDER } from 'constants/featureFlags';
import useProjectUsers from 'components/projects/users/hooks/useProjectUsers';
import useCreateProjectPage from './useCreateProjectPage';
import useGetProjectContacts from 'data/v1/projects/hooks/useGetProjectContacts';
import { ExamplePageId, getExamplePages } from '../examples/examplePages';
import mixContentWithContext from '../helpers/mixContentWithContext';

type UseCreateExamplePage = {
  projectDbId: number;
  projectTitle: string;
  onSuccess: (pageNodeId: string, pageDbId: number) => void;
  showInvoiceFeedbackBanner: (exampleKey: ExamplePageId) => void;
};

type CreatedPageType = {
  id: string;
  databaseId: number;
};

const useCreateExamplePage = ({
  projectDbId,
  projectTitle,
  onSuccess,
  showInvoiceFeedbackBanner,
}: UseCreateExamplePage) => {
  const projectNodeId = graphQLIdGenerator('Location', projectDbId);

  const { projectUsers } = useProjectUsers({
    projectId: projectNodeId,
  });

  const currentUser = useCurrentUser();
  const { currentCompany } = useCurrentCompany();

  const { contacts } = useGetProjectContacts({
    projectDbId,
  });

  const { createProjectPage, loading: isLoading } = useCreateProjectPage({
    projectNodeId,
    onSuccess: (page: CreatedPageType) => onSuccess(page.id, page.databaseId),
  });

  // Invoice Page Beta uses Document Builder flag to serve to same beta group
  const { [LD_DOCUMENTS_DOCUMENT_BUILDER]: isBetaFlagEnabled } = useFlags();

  const createExamplePage = (exampleId: ExamplePageId) => {
    const examplePage = getExamplePages({
      id: exampleId,
      includePagesBetaFeatures: isBetaFlagEnabled,
    });

    if (examplePage) {
      const rawContent = JSON.stringify(examplePage.body);
      const mixedContent = mixContentWithContext(rawContent, {
        currentUser,
        project: {
          name: projectTitle,
          title: projectTitle,
        },
        contact: contacts?.length ? contacts[0] : null,
        projectUsers,
        company: currentCompany,
      });

      showInvoiceFeedbackBanner(exampleId);

      createProjectPage({
        title: examplePage.title,
        tiptapContent: mixedContent,
      });

      steven.elementClicked({
        id: 'project_page_create_page',
        content: examplePage.title,
        type: ELEMENT_TYPES.BUTTON,
        action: 'project_page_create_page_clicked',
      });
    }
  };

  return {
    createExamplePage,
    isLoading,
  };
};

export default useCreateExamplePage;
